import { HttpClient } from '@angular/common/http';
import { ElementRef, inject, Injectable } from '@angular/core';
import { ApplePayEventsEnum, GooglePayEventsEnum, Stripe as CapacitorStripe } from '@capacitor-community/stripe';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { Config } from '@luggagehero/shared/environment';
import { IBankAccount, InitiatePaymentResult } from '@luggagehero/shared/interfaces';
import { SharedLoggingService } from '@luggagehero/shared/services/logging';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { SharedUtilString } from '@luggagehero/shared/util';
import {
  ConfirmCardPaymentData,
  CreateTokenBankAccountData,
  CreateTokenCardData,
  PaymentIntentOrSetupIntentResult,
  PaymentMethod,
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
  Stripe as StripeJs,
  StripeCardElement,
  StripeElements,
  TokenResult,
} from '@stripe/stripe-js';
// `loadStripe` from `/pure`, NOT the main entry: the main `@stripe/stripe-js` entry injects the
// Stripe.js <script> into <head> the moment it is *imported* (for fraud signals), so merely
// bundling this service pulls ~294 KB of Stripe onto every page that loads it — including the
// prerendered landing /stores pages, which never touch payment until a traveler reaches the wizard's
// Confirm step. `/pure`'s loadStripe loads Stripe only when *called*, so landing stays Stripe-free
// until payment. Apps that want Stripe eager (web-traveler, ionic-traveler) call init()/preload() at
// startup — unchanged. Type-only imports above stay on the main entry (types carry no runtime inject).
import { loadStripe } from '@stripe/stripe-js/pure';
import { firstValueFrom } from 'rxjs';

/**
 * How a native wallet sheet (Apple Pay / Google Pay through the Capacitor plugin) ended. A string union
 * rather than a boolean pair: the apps compile lib sources without `strictNullChecks`, where only string
 * discriminants narrow.
 */
export type NativeWalletResult = 'completed' | 'canceled' | 'failed';

@Injectable({
  providedIn: 'root',
})
export class SharedStripeService {
  private window = inject(SharedWindowService);
  private http = inject(HttpClient);
  private log = inject(SharedLoggingService);

  private stripe: StripeJs;
  private elements: StripeElements;
  private initComplete = false;
  private initTask: Promise<void> | null = null;
  /** The in-flight/settled Stripe.js load, shared between preload() and init() so the script loads once. */
  private stripeLoad: Promise<StripeJs | null> | null = null;
  private _isWalletPaymentAvailable = false;
  private _isApplePayAvailable = false;
  private _isGooglePayAvailable = false;

  public get isWalletPaymentAvailable(): boolean {
    return this._isWalletPaymentAvailable;
  }

  public get isApplePayAvailable(): boolean {
    return this._isApplePayAvailable;
  }

  public get isGooglePayAvailable(): boolean {
    return this._isGooglePayAvailable;
  }

  /**
   * Kick off just the Stripe.js *script* download, early and fire-and-forget, without running the
   * heavier `init()` (Elements/wallet probes). Since we load via `@stripe/stripe-js/pure` the script
   * is no longer auto-injected on import, so this restores the "request Stripe.js early for fraud
   * signals" timing on apps that want it eager (web-traveler, ionic-traveler) — WITHOUT bringing that
   * eager request back to the prerendered landing pages, which never call this. Idempotent and shared
   * with `init()`: both resolve the same cached `loadStripe()` promise, so calling `preload()` then
   * `init()` downloads the script once. Browser-only in effect (loadStripe no-ops under SSR).
   */
  preload(): void {
    if (this.initComplete || this.stripeLoad != null) {
      return;
    }
    // Hold the promise so init() reuses it instead of starting a second load. Swallow here; init()
    // surfaces/logs any real failure when it actually needs Stripe.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- @stripe/stripe-js/pure's loadStripe return type resolves as `any` under this lib's strict typed-lint config (its types chain pure/index.d.ts -> dist/pure.d.ts, which the resolver doesn't fully follow here); the loader is runtime-identical to the main entry and init() consumes the result as StripeJs
    this.stripeLoad = loadStripe(Config.environment.STRIPE_PUBLISHABLE_KEY).catch(() => null);
  }

  async init(): Promise<void> {
    if (this.initComplete) {
      return;
    }
    if (this.initTask == null) {
      this.initTask = this._init();
    }
    try {
      await this.initTask;
    } catch (err) {
      this.initTask = null;
      throw err;
    }
    this.initComplete = true;
  }

  private async _init(): Promise<void> {
    let stripe: StripeJs | null = null;
    let lastError: Error | undefined;

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        // Reuse a preload()'d load on the first attempt so the script isn't fetched twice; fall back
        // to a fresh load (and on retry always a fresh one, since a failed preload resolved to null).
        if (attempt === 0 && this.stripeLoad != null) {
          stripe = await this.stripeLoad;
        }
        if (!stripe) {
          stripe = await loadStripe(Config.environment.STRIPE_PUBLISHABLE_KEY);
        }
        if (stripe) {
          break;
        }
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
      }
    }

    if (!stripe) {
      const msg = lastError?.message || 'Failed to load Stripe.js';
      void this.log.error(`Error initializing Stripe: ${msg}`, lastError);
      throw new Error(`Error initializing Stripe: ${msg}`);
    }

    this.stripe = stripe;

    if (!Config.isProduction) {
      console.log(`Stripe initialized correctly: ${this.stripe ? 'Yes' : 'NO'}`);
    }
    // On native the plugin must be configured before a wallet sheet can open, so it is awaited there; a
    // failure only takes the native wallets away, never the card path. Web keeps the old fire-and-forget.
    let isNativeStripeReady = false;
    if (this.window.isNative) {
      isNativeStripeReady = await this.initializeNativeStripe();
    } else {
      void CapacitorStripe.initialize({
        publishableKey: Config.environment.STRIPE_PUBLISHABLE_KEY,
      });
    }

    //
    // Initialize Stripe elements
    //
    let fontUrl = AppConfig.STRIPE_ELEMENTS_FONT_URL;

    if (this.window.isNative) {
      try {
        const fontBuffer = await firstValueFrom(this.http.get(fontUrl, { responseType: 'arraybuffer' }));
        const fontBase64 = SharedUtilString.toBase64String(fontBuffer);
        fontUrl = `data:font/woff;base64,${fontBase64}`;
      } catch {
        //
        // Ignore error and continue. If we fail to load the font and prepare a base64 encoded data URL, we will pass
        // the relative URL for the font file to Stripe instead. This will work on web but fail on capacitor because
        // Stripe Elements does not support capacitor:// as a URL scheme.
        //
        // All this means is that _if_ we fail to load the font as base64 on a native device (which shouldn't happen),
        // we will just eventually fall back to a default font for Stripe Elements.
        //
      }

      this._isApplePayAvailable = isNativeStripeReady && (await this.checkNativeApplePay());
      this._isGooglePayAvailable = isNativeStripeReady && (await this.checkNativeGooglePay());

      this._isWalletPaymentAvailable =
        (this._isApplePayAvailable && AppConfig.IS_APPLE_PAY_ENABLED) ||
        (this._isGooglePayAvailable && AppConfig.IS_GOOGLE_PAY_ENABLED);
    } else {
      // Create a dummy payment request which will determine wallet availability
      await this.createBrowserPaymentRequest(0, 'usd', AppConfig.MERCHANT_COUNTRY_CODE, 'Dummy charge');
    }

    this.elements = this.stripe.elements({
      fonts: [
        {
          family: 'Eina',
          src: `url('${fontUrl}')`,
          style: 'normal',
        },
      ],
    });

    if (!Config.isProduction) {
      console.log(`Stripe ready`, {
        isWalletPaymentAvailable: this._isWalletPaymentAvailable,
        isApplePayAvailable: this._isApplePayAvailable,
        isGooglePayAvailable: this._isGooglePayAvailable,
      });
    }
  }

  private async initializeNativeStripe(): Promise<boolean> {
    try {
      await CapacitorStripe.initialize({
        publishableKey: Config.environment.STRIPE_PUBLISHABLE_KEY,
      });
      return true;
    } catch (err) {
      void this.log.error('Error initializing the native Stripe plugin', err);
      return false;
    }
  }

  async checkNativeApplePay(): Promise<boolean> {
    if (!AppConfig.IS_APPLE_PAY_ENABLED) {
      return false;
    }
    try {
      await CapacitorStripe.isApplePayAvailable();
    } catch {
      return false;
    }
    return true;
  }

  async checkNativeGooglePay(): Promise<boolean> {
    if (!AppConfig.IS_GOOGLE_PAY_ENABLED) {
      return false;
    }
    try {
      await CapacitorStripe.isGooglePayAvailable();
    } catch {
      return false;
    }
    return true;
  }

  async createCardElement(container: ElementRef<HTMLElement>): Promise<StripeCardElement> {
    await this.init();

    const cardElement = this.elements.create('card', {
      iconStyle: 'default',
      hidePostalCode: true,
      style: {
        base: {
          iconColor: '#1dafe0',
          color: '#384347',
          fontFamily: '"Eina", "Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif',
          fontSize: '16px',

          '::placeholder': {
            color: '#656D70',
          },
        },
        invalid: {
          iconColor: '#A94442',
          color: '#A94442',
        },
      },
    });
    // Add an instance of the card UI component into the card container <div>
    cardElement.mount(container.nativeElement);

    return cardElement;
  }

  public async requestBrowserPayment(
    amount: number,
    currency: string,
    country: string,
    label: string,
  ): Promise<PaymentRequest> {
    await this.init();

    if (this.window.isNative) {
      // The WebView has no Payment Request API, and a failed attempt below would clear the wallet flags
      // the native plugin set — leaving a wallet selected that is no longer offered (AM-4995). Native
      // wallets pay through `payWithApplePay` / `payWithGooglePay` instead.
      return null;
    }

    return this.createBrowserPaymentRequest(amount, currency, country, label);
  }

  /**
   * Authorize an intent with Apple Pay through the native plugin. The plugin confirms the intent's client
   * secret itself; the caller attaches the confirmed intent afterwards, as with the browser wallet.
   * @param payment The intent to confirm — captured by the caller at tap time, never re-read after.
   * @param label The summary line shown in the sheet.
   */
  public async payWithApplePay(payment: InitiatePaymentResult, label: string): Promise<NativeWalletResult> {
    return this.payWithNativeWallet(payment, 'apple', async (clientSecret) => {
      await CapacitorStripe.createApplePay({
        paymentIntentClientSecret: clientSecret,
        merchantIdentifier: AppConfig.APPLE_PAY_MERCHANT_ID,
        requiredShippingContactFields: ['emailAddress'],
        paymentSummaryItems: [{ label, amount: toWalletAmount(payment.amount) }],
        countryCode: AppConfig.MERCHANT_COUNTRY_CODE,
        currency: payment.currency || AppConfig.MERCHANT_CURRENCY,
      });
      const { paymentResult } = await CapacitorStripe.presentApplePay();
      return paymentResult === ApplePayEventsEnum.Completed
        ? 'completed'
        : paymentResult === ApplePayEventsEnum.Canceled
          ? 'canceled'
          : 'failed';
    });
  }

  /**
   * Authorize an intent with Google Pay through the native plugin. Android reads the amount from the
   * intent itself; only the secret and currency are passed.
   * @param payment The intent to confirm — captured by the caller at tap time, never re-read after.
   */
  public async payWithGooglePay(payment: InitiatePaymentResult): Promise<NativeWalletResult> {
    return this.payWithNativeWallet(payment, 'google', async (clientSecret) => {
      await CapacitorStripe.createGooglePay({
        paymentIntentClientSecret: clientSecret,
        currency: payment.currency || AppConfig.MERCHANT_CURRENCY,
      });
      const { paymentResult } = await CapacitorStripe.presentGooglePay();
      return paymentResult === GooglePayEventsEnum.Completed
        ? 'completed'
        : paymentResult === GooglePayEventsEnum.Canceled
          ? 'canceled'
          : 'failed';
    });
  }

  private async payWithNativeWallet(
    payment: InitiatePaymentResult,
    wallet: 'apple' | 'google',
    present: (clientSecret: string) => Promise<NativeWalletResult>,
  ): Promise<NativeWalletResult> {
    try {
      // Awaited first, so the plugin is configured before any sheet is created.
      await this.init();
      const isAvailable = wallet === 'apple' ? this._isApplePayAvailable : this._isGooglePayAvailable;
      if (!this.window.isNative || !isAvailable || !payment?.client_secret) {
        return 'failed';
      }
      return await present(payment.client_secret);
    } catch (err) {
      void this.log.error(`Native ${wallet} pay failed`, err);
      return 'failed';
    }
  }

  private async createBrowserPaymentRequest(
    amount: number,
    currency: string,
    country: string,
    label: string,
  ): Promise<PaymentRequest> {
    let browserPaymentRequest: PaymentRequest;

    try {
      browserPaymentRequest = this.stripe.paymentRequest({
        country,
        currency,
        total: { label, amount },
        requestPayerName: false,
        requestPayerEmail: true,
      });

      if (browserPaymentRequest) {
        const browserPaymentInfo = await browserPaymentRequest.canMakePayment();

        if (browserPaymentInfo) {
          this._isApplePayAvailable = browserPaymentInfo.applePay ? true : false;
          // If both Apple and Google Pay should be available, Apple wins for now (our UI only supports one provider)
          this._isGooglePayAvailable = !browserPaymentInfo.applePay && browserPaymentInfo.googlePay ? true : false;
          this._isWalletPaymentAvailable = this._isApplePayAvailable || this._isGooglePayAvailable;

          return browserPaymentRequest;
        }
      }
    } catch (err) {
      if (!Config.isProduction) {
        console.log(`Error checking for browser payment availability`, err);
      }
    }

    this._isApplePayAvailable = false;
    this._isGooglePayAvailable = false;
    this._isWalletPaymentAvailable = false;

    return null;
  }

  /**
   * Handles the payment method event from the browser payment request. Follows the flow described here:
   * https://stripe.com/docs/stripe-js/elements/payment-request-button?client=html#html-js-complete-payment
   * @param clientSecret The client secret of the PaymentIntent or SetupIntent to confirm.
   * @param e The payment method event from the browser payment request.
   * @returns The payment method that was confirmed.
   * @throws An error if the payment failed.
   */
  async confirmBrowserPayment(clientSecret: string, e: PaymentRequestPaymentMethodEvent): Promise<PaymentMethod> {
    await this.init();

    // Confirm the PaymentIntent without handling potential next actions (yet).
    const { error, paymentIntent, setupIntent } = await this.confirmPayment(clientSecret, e.paymentMethod.id, false);

    let errorMessage: string;

    if (error) {
      //
      // Report to the browser that the payment failed, prompting it to re-show the payment interface, or show an error
      // message and close the payment interface.
      //
      e.complete('fail');

      errorMessage = error.message;
    } else {
      //
      // Report to the browser that the confirmation was successful, prompting it to close the browser payment method
      // collection interface.
      //
      e.complete('success');

      //
      // Check if the PaymentIntent requires any actions and, if so, let Stripe.js
      // handle the flow.
      //
      if (paymentIntent?.status === 'requires_action' || setupIntent?.status === 'requires_action') {
        // Let Stripe.js handle the rest of the payment flow.
        const { error } = await this.confirmPayment(clientSecret);
        if (error) {
          errorMessage = error.message;
        }
      }
    }

    if (errorMessage) {
      throw new Error(errorMessage);
    }

    return e.paymentMethod;
  }

  async tokenizeCardElement(element: StripeCardElement, tokenData?: CreateTokenCardData): Promise<TokenResult> {
    await this.init();

    return this.stripe.createToken(element, tokenData);
  }

  async tokenizeBankAccount(value: IBankAccount): Promise<TokenResult> {
    await this.init();

    const bankAccountData: CreateTokenBankAccountData = {
      country: value.country,
      currency: value.currency,
      account_number: value.accountNumber,
      // TODO: Validate that hardcoding 'company' here does not cause problems
      account_holder_type: value.accountHolderType || 'company',
    };
    if (value.routingNumber) {
      bankAccountData.routing_number = value.routingNumber;
    }
    if (value.accountHolderName) {
      bankAccountData.account_holder_name = value.accountHolderName;
    }

    const res = await this.stripe.createToken('bank_account', bankAccountData);

    return res;
  }

  async confirmPayment(
    clientSecret: string,
    paymentMethodIdOrCardElement?: string | StripeCardElement,
    handleActions = true,
  ): Promise<PaymentIntentOrSetupIntentResult> {
    await this.init();

    const data: ConfirmCardPaymentData = paymentMethodIdOrCardElement
      ? {
          payment_method:
            typeof paymentMethodIdOrCardElement === 'string'
              ? paymentMethodIdOrCardElement
              : { card: paymentMethodIdOrCardElement },
        }
      : undefined;

    const res = clientSecret.startsWith('seti_')
      ? await this.stripe.confirmCardSetup(clientSecret, data, { handleActions })
      : await this.stripe.confirmCardPayment(clientSecret, data, { handleActions });

    return res;
  }
}

/** A wallet sheet shows major units with at most two decimals, and never a negative total. */
const toWalletAmount = (amount: number | undefined): number => Math.max(0, Math.round((amount || 0) * 100) / 100);
